import React, { createContext, useEffect, useRef, useState, useContext, useMemo } from 'react';
import { TicketContext } from '../../app/providers/TicketContext';
import { fetchWrapper } from '../../shared/api/fetchWrapper';
import { useNavigate } from 'react-router-dom';
import { safeParseJson } from '../../shared/security/safeJson';
import { redactSensitive } from '../../shared/security/redactSensitive';
import { isEventDisabled } from '../../shared/utils/eventUtils';

export const TicketWebSocketContext = createContext();

export const TicketWebSocketProvider = ({ children }) => {
  const socketRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [eveId, setEveId] = useState(null);
  const [ticId, setTicId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [notificationCounters, setNotificationCounters] = useState({});
  const pendingFetchByTicketRef = useRef({});
  const flushFetchTimerRef = useRef(null);

  const wsUrl =
    process.env.REACT_APP_TICKET_WS_URL ||
    process.env.REACT_APP_WS_URL ||
    "wss://v2.soylemez.diagrun.com/ws/";
  const IS_PROD = process.env.NODE_ENV === 'production';
  const [visData, setVisData] = useState(null);
  const [loekz, setLoekz] = useState(null);
  const [ticketsToMoveToTop, setTicketsToMoveToTop] = useState(new Set());
  const [pendingEvents, setPendingEvents] = useState(new Set()); // Bekleyen event'ler
  
  const [networkLogs, setNetworkLogs] = useState([]);
  const [websocketLogs, setWebsocketLogs] = useState([]);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [logFilters, setLogFilters] = useState({
    showRequests: true,
    showResponses: true,
    showWebSocket: true,
    showErrors: true
  });
  
  const { 
    selectTicket, 
    tickets,
    selectedTicketId, 
    setSelectedTicketId,
    setSelectedTicket,
    events, 
    addEventToCache, 
    deleteEventFromCache, 
    eventCache, 
    setEvents, 
 
    setRightPanelToolbarElements,
    setAllFields,
    setTickets,
    SendEvent,
    setCurrentInputField,
    markSendPerformanceStage
  } = useContext(TicketContext);
  
  const selectedTicketIdRef = useRef(selectedTicketId);
  const isProcessingRef = useRef(false);
  const processedEventsRef = useRef(new Set());
  const fetchingEventsRef = useRef(new Set());
  const lastFetchTimeRef = useRef({});
  const processedStreamersRef = useRef(new Map());
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isReconnectingRef = useRef(false);
  const shouldReconnectRef = useRef(true); // Component unmount olduğunda reconnect'i durdurmak için
  const lastSequenceByTicketRef = useRef({});

  const FETCH_AGGREGATION_WINDOW_MS = 50;

  if (!(processedStreamersRef.current instanceof Map)) {
    processedStreamersRef.current = new Map();
  }
  const navigate = useNavigate();

  const addNetworkLog = (type, data, timestamp = Date.now()) => {
    const log = {
      id: Date.now() + Math.random(),
      type,
      data: IS_PROD ? redactSensitive(data) : data,
      timestamp,
      formattedTime: new Date(timestamp).toLocaleTimeString('tr-TR')
    };
    
    setNetworkLogs(prev => [log, ...prev.slice(0, 999)]); // Son 1000 log'u tut
  };

  const addWebSocketLog = (type, data, timestamp = Date.now()) => {
    const log = {
      id: Date.now() + Math.random(),
      type,
      data: IS_PROD ? redactSensitive(data) : data,
      timestamp,
      formattedTime: new Date(timestamp).toLocaleTimeString('tr-TR'),
      source: 'TicketWebSocket', // WebSocket kaynağını belirt
      function: getCallingFunction() // Çağıran fonksiyonu belirt
    };
    
    setWebsocketLogs(prev => [log, ...prev.slice(0, 999)]); // Son 1000 log'u tut
  };

  const getCallingFunction = () => {
    try {
      const stack = new Error().stack;
      const lines = stack.split('\n');
      
      // TicketWebSocketProvider içindeki fonksiyonları bul
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.includes('TicketWebSocketProvider') || line.includes('connect') || line.includes('onmessage')) {
          continue; // Provider fonksiyonlarını atla
        }
        
        if (line.includes('fetchEventFromServer')) {
          return 'fetchEventFromServer';
        } else if (line.includes('onopen')) {
          return 'WebSocket.onopen';
        } else if (line.includes('onclose')) {
          return 'WebSocket.onclose';
        } else if (line.includes('onerror')) {
          return 'WebSocket.onerror';
        } else if (line.includes('onmessage')) {
          return 'WebSocket.onmessage';
        }
      }
      
      return 'Unknown Function';
    } catch (error) {
      return 'Error Detecting Function';
    }
  };

  const originalFetchWrapper = fetchWrapper;
  const enhancedFetchWrapper = async (processId, body, navigate) => {
  const requestId = Date.now() + Math.random();
    
    // Request log'u ekle
    addNetworkLog('REQUEST', {
      processId,
      body,
      requestId,
      url: process.env.REACT_APP_API_URL || 'API_URL'
    });
    

    
    try {
      const response = await originalFetchWrapper(processId, body, navigate);
      
      // Response log'u ekle
      addNetworkLog('RESPONSE', {
        processId,
        response,
        requestId,
        success: true
      });
      
      // fetchWrapper zaten debug log ekliyor, burada ek bir şey yapmaya gerek yok
      
      return response;
    } catch (error) {
      // Error log'u ekle
      addNetworkLog('ERROR', {
        processId,
        error: error.message,
        requestId,
        success: false
      });
      
      throw error;
    }
  };

  if (!IS_PROD && typeof window !== 'undefined') {
    window.enhancedFetchWrapper = enhancedFetchWrapper;
  }

  useEffect(() => {
    selectedTicketIdRef.current = selectedTicketId;
    processedEventsRef.current.clear();
  }, [selectedTicketId]);

  const moveTicketToTop = (ticketId) => {
    setTicketsToMoveToTop(prev => new Set([...prev, ticketId]));
    
    setTickets(prevTickets => {
      const updatedTicket = prevTickets.find(t => String(t.TICID) === String(ticketId));
      if (!updatedTicket) return prevTickets;
      
      const otherTickets = prevTickets.filter(t => String(t.TICID) !== String(ticketId));
      return [updatedTicket, ...otherTickets];
    });
    
    setTimeout(() => {
      setTicketsToMoveToTop(prev => {
        const newSet = new Set(prev);
        newSet.delete(ticketId);
        return newSet;
      });
    }, 8000);
  };

  const fetchEventFromServer = async (ticId, eveIdParam, streamerIdParam = null) => {
    const currentTicketId = selectedTicketIdRef.current;
    if (!currentTicketId || parseInt(String(currentTicketId)) !== parseInt(String(ticId))) {
      return;
    }
    const eveIdList = Array.isArray(eveIdParam) ? eveIdParam : [eveIdParam];
    const uniqueEveIds = Array.from(new Set(eveIdList.filter(id => id !== undefined && id !== null))).map(id => String(id));

    if (uniqueEveIds.length === 0) {
      return;
    }

    const sortedNumericIds = uniqueEveIds
      .map(id => parseInt(id, 10))
      .filter(id => !Number.isNaN(id))
      .sort((a, b) => a - b);

    const primaryEveId = sortedNumericIds.length > 0 ? sortedNumericIds[sortedNumericIds.length - 1] : parseInt(uniqueEveIds[uniqueEveIds.length - 1], 10);

    if (!primaryEveId || Number.isNaN(primaryEveId)) {
      return;
    }

    const now = Date.now();
    const lastFetchKey = `eve-${primaryEveId}`;
    const lastFetchTime = lastFetchTimeRef.current[lastFetchKey] || 0;
    const timeSinceLastFetch = now - lastFetchTime;

    if (fetchingEventsRef.current.has(lastFetchKey)) {
      return;
    }


    setPendingEvents(prev => {
      const newSet = new Set(prev);
      uniqueEveIds.forEach(id => newSet.add(id));
      return newSet;
    });

    fetchingEventsRef.current.add(lastFetchKey);
    lastFetchTimeRef.current[lastFetchKey] = now;

    try {
    const requestBody = {
        token: localStorage.getItem('token'),
        process: 100022958,
        TICID: ticId,
        EVEID: primaryEveId,
        layoutParams: ''
      };
      if (uniqueEveIds.length > 0) {
        requestBody.EVEIDS = uniqueEveIds;
      }
      if (streamerIdParam) {
        requestBody.STREAMER_ID = streamerIdParam;
      }

      const result = await enhancedFetchWrapper(100022958, requestBody, navigate);

              if (result.result) {
          const currentTicketIdAfterFetch = selectedTicketIdRef.current;
          if (!currentTicketIdAfterFetch || parseInt(String(currentTicketIdAfterFetch)) !== parseInt(String(ticId))) {
          } else {
            if (result.new_request) {
              const { ELMNT, DTYPE, DFLEN, ELMDC, CTABL, READONLY, REQUIRED, ACTION } = result.new_request;
              
              setCurrentInputField(result.new_request);

              if (ACTION && ACTION.DRC) {
                SendEvent({
                  inputName: ELMNT,
                  message: `-${ACTION.DRC} ${ACTION.data.mission}`,
                }, []);
              }
            }

            if (result.toolbar) {
              setRightPanelToolbarElements(result.toolbar);
            }

            if (result.all_fields) {
              setAllFields(result.all_fields);
            }
          }

        const normalizedEvents = [];

        if (Array.isArray(result.events) && result.events.length > 0) {
          result.events.forEach((serverEvent, index) => {
            const visibleValue = serverEvent.VISIBLE;
            const isVisible = visibleValue === 1 || visibleValue === "1" || visibleValue === true;
            
            const loekzValue = serverEvent.LOEKZ;
            const isDeleted = loekzValue === 1 || loekzValue === "1" || loekzValue === true || 
                              loekzValue === "X" || loekzValue === "x";
            
            normalizedEvents.push({
              EVEID: serverEvent.EVEID,
              TICID: serverEvent.TICID,
              MEMID: serverEvent.MEMID,
              ETYPE: serverEvent.ETYPE,
              FLOWID: serverEvent.FLOWID,
              ESOURCEFILE: serverEvent.ESOURCEFILE,
              ANDAT: serverEvent.ANDAT,
              TDESC: serverEvent.TDESC,
              RONLY: serverEvent.RONLY,
              ROWID: serverEvent.ROWID,
              FNAME: serverEvent.FNAME,
              ITYPE: serverEvent.ITYPE,
              FDESC: serverEvent.FDESC,
              FVALUE: serverEvent.FVALUE,
              ALLVALUES: serverEvent.ALLVALUES,
              VALIDATE: serverEvent.VALIDATE,
              VISIBLE: serverEvent.VISIBLE,
              COMPLATED: serverEvent.COMPLATED,
              LOEKZ: serverEvent.LOEKZ,
              LONAM: serverEvent.LONAM,
              LODAT: serverEvent.LODAT,
              member: serverEvent.member,
              position: serverEvent.position,
              INPA_CONF: serverEvent.INPA_CONF,
              ATTACHES: serverEvent.ATTACHES || [],
              TOWNER_IMAGE: serverEvent.TOWNER_IMAGE,
              PHOTO_SWITCH: serverEvent.PHOTO_SWITCH,
              UNAME_SWITCH: serverEvent.UNAME_SWITCH,
              FNAME_SWITCH: serverEvent.FNAME_SWITCH,
              FVALUE_SWITCH: serverEvent.FVALUE_SWITCH,
              TDESC_SWITCH: serverEvent.TDESC_SWITCH,
              ESOURCEFILE_SWITCH: serverEvent.ESOURCEFILE_SWITCH
            });
          });
        } else if (result.event) {
          normalizedEvents.push(result.event);
        } else if (result.data && result.data.event) {
          normalizedEvents.push(result.data.event);
        } else {
          normalizedEvents.push({
            EVEID: primaryEveId,
            TICID: parseInt(ticId, 10),
            FNAME: 'WEBSOCKET_EVENT',
            FVALUE: 'WebSocket\'ten gelen event',
            TDESC: 'WebSocket event',
            ANDAT: new Date().toLocaleString('tr-TR'),
            VALIDATE: 1,
            UNAME_SWITCH: 1,
            TDESC_SWITCH: 1,
            FVALUE_SWITCH: 1,
            PHOTO_SWITCH: 0,
            ESOURCEFILE_SWITCH: 0,
            position: 'L',
            member: {
              uname: 'WebSocket User',
              memid: 'websocket_user',
              pp_path: null
            },
            ATTACHES: []
          });
        }

        if (normalizedEvents.length > 0) {
          const additionalDataWithNewRequest = {
            ...result,
            new_request: result.new_request
          };
          
          normalizedEvents.forEach(eventData => {
            if (eventData && eventData.EVEID) {
              addEventToCache(ticId, eventData, additionalDataWithNewRequest);
            }
          });

          setTickets(prevTickets => {
            return prevTickets.map(ticket => {
              if (String(ticket.TICID) === String(ticId)) {
                return {
                  ...ticket,
                  ANDAT: normalizedEvents[normalizedEvents.length - 1].ANDAT || new Date().toISOString(),
                  lastEventDate: normalizedEvents[normalizedEvents.length - 1].ANDAT || new Date().toISOString()
                };
              }
              return ticket;
            });
          });

          markSendPerformanceStage(ticId, 'wsFetched', {
            streamerId: streamerIdParam,
            fetchedIds: normalizedEvents.map((ev) => ev.EVEID),
          });

          const currentTicketIdForEvents = selectedTicketIdRef.current;
          if (currentTicketIdForEvents && parseInt(String(currentTicketIdForEvents), 10) === parseInt(String(ticId), 10)) {
            setEvents(prevEvents => {
              const eventMap = new Map();

              prevEvents.forEach(event => {
                eventMap.set(String(event.EVEID), event);
              });

              normalizedEvents.forEach(eventData => {
                const existing = eventMap.get(String(eventData.EVEID));
                if (existing && isEventDisabled(existing)) {
                  return;
                }
                eventMap.set(String(eventData.EVEID), eventData);
              });

              const sortedEvents = Array.from(eventMap.values()).sort((a, b) => {
                const aId = parseInt(a.EVEID) || 0;
                const bId = parseInt(b.EVEID) || 0;
                return aId - bId;
              });

              return sortedEvents;
            });
          } else {
          }
        }
      }
    } catch (error) {
    } finally {
      fetchingEventsRef.current.delete(lastFetchKey);
      setPendingEvents(prev => {
        const newSet = new Set(prev);
        uniqueEveIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    }
  };

  const flushPendingFetches = () => {
    flushFetchTimerRef.current = null;

    const snapshot = pendingFetchByTicketRef.current;
    pendingFetchByTicketRef.current = {};

    Object.entries(snapshot).forEach(([ticketId, entry]) => {
      if (!entry || !entry.eveIds || entry.eveIds.size === 0) return;

      const mergedIds = Array.from(entry.eveIds);
      const streamerId = entry.streamerId || null;

      fetchEventFromServer(ticketId, mergedIds, streamerId);
    });
  };

  const queueFetchEvents = (ticketId, ids, streamerId) => {
    if (!ticketId || !Array.isArray(ids) || ids.length === 0) return;

    const ticketKey = String(ticketId);
    let entry = pendingFetchByTicketRef.current[ticketKey];
    if (!entry) {
      entry = { eveIds: new Set(), streamerId: null };
      pendingFetchByTicketRef.current[ticketKey] = entry;
    }

    for (const id of ids) {
      if (id !== undefined && id !== null) {
        entry.eveIds.add(String(id));
      }
    }

    if (streamerId) {
      entry.streamerId = String(streamerId);
    }

    if (flushFetchTimerRef.current === null) {
      flushFetchTimerRef.current = setTimeout(
        flushPendingFetches,
        FETCH_AGGREGATION_WINDOW_MS,
      );
    }
  };

  const cleanupProcessedStreamers = (ttl = 60000) => {
    const now = Date.now();
    processedStreamersRef.current.forEach((ts, key) => {
      if (now - ts > ttl) {
        processedStreamersRef.current.delete(key);
      }
    });
  };

  const handleEventBatch = (batchData) => {
    if (!batchData || !Array.isArray(batchData.events) || batchData.events.length === 0) {
      const emptyStreamerId = String(batchData?.streamer_id || batchData?.STREAMER_ID || '');
      if (emptyStreamerId) {
        processedStreamersRef.current.set(emptyStreamerId, Date.now());
      }
      return;
    }
    const streamerId = String(batchData.streamer_id || batchData.STREAMER_ID || '');

    cleanupProcessedStreamers();

    if (!Array.isArray(batchData.events) || batchData.events.length === 0) {
      if (streamerId) {
        processedStreamersRef.current.set(streamerId, Date.now());
      }
      return;
    }
    batchData.events = batchData.events.filter(evt => {
      const eventId = evt.eveid || evt.EVEID;
      const ticketId = String(evt.ticid || evt.TICID || '');
      const dedupKey = String(
        evt.dedup_key ||
        `${eventId}:${evt.ui_action || evt.event_type || ''}`
      );

      if (!eventId || !dedupKey) return false;

      if (processedEventsRef.current.has(dedupKey)) {
        return false;
      }

      const seq = Number(evt.sequence);
      if (Number.isFinite(seq) && ticketId) {
        const lastSeq = lastSequenceByTicketRef.current[ticketId];
        const isStale = typeof lastSeq === 'number' && seq <= lastSeq;
        const isRemove = String(evt.ui_action || '').toLowerCase() === 'remove';

        if (isStale && !isRemove) {
          return false;
        }

        if (typeof lastSeq !== 'number' || seq > lastSeq) {
          lastSequenceByTicketRef.current[ticketId] = seq;
        }
      }

      return true;
    });
    
    if (batchData.events.length === 0) {
      return;
    }
    const eventsByTicket = batchData.events.reduce((acc, evt) => {
      const ticketId = evt.ticid || evt.TICID;
      const eventId = evt.eveid || evt.EVEID;

      if (!ticketId || !eventId) {
        return acc;
      }

      const key = String(ticketId);
      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(String(eventId));
      return acc;
    }, {});
    const dedupKeysByEventId = batchData.events.reduce((acc, evt) => {
      const eventId = String(evt.eveid || evt.EVEID || '');
      const dedupKey = String(
        evt.dedup_key ||
        `${eventId}:${evt.ui_action || evt.event_type || ''}`
      );
    
      if (eventId && dedupKey) {
        acc[eventId] = dedupKey;
      }
    
      return acc;
    }, {});
    Object.entries(eventsByTicket).forEach(([ticketId, eventIds]) => {
      const idsToFetch = eventIds.filter(id => {
        const dedupKey = dedupKeysByEventId[String(id)];
        return dedupKey && !processedEventsRef.current.has(dedupKey);
      });

      const isCurrentTicket = selectedTicketIdRef.current &&
        parseInt(selectedTicketIdRef.current, 10) === parseInt(ticketId, 10);

      moveTicketToTop(ticketId);

      if (!isCurrentTicket) {
        setNotificationCounters(prev => {
          const newCounters = { ...prev };
          newCounters[ticketId] = (newCounters[ticketId] || 0) + idsToFetch.length;
          return newCounters;
        });
        idsToFetch.forEach(id => {
          const dedupKey = dedupKeysByEventId[String(id)];
          if (dedupKey) {
            processedEventsRef.current.add(dedupKey);
          }
        });
        if (streamerId) {
          processedStreamersRef.current.set(streamerId, Date.now());
        }
        return;
      }

      
      markSendPerformanceStage(ticketId, 'wsMessage', {
        streamerId: batchData.streamer_id || batchData.STREAMER_ID || null,
        eventCount: idsToFetch.length,
      });

      if (idsToFetch.length === 0) {
        return;
      }
      
      const fetchIds = idsToFetch;

      fetchIds.forEach(id => {
        const dedupKey = dedupKeysByEventId[String(id)];
        if (dedupKey) {
          processedEventsRef.current.add(dedupKey);
        }
      });
      if (streamerId) {
        processedStreamersRef.current.set(streamerId, Date.now());
      }

      queueFetchEvents(ticketId, fetchIds, streamerId);
    });
  };

  const sendTicketSubscriptions = (socket, ticketsList = []) => {

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return;
    }
  
    if (!Array.isArray(ticketsList) || ticketsList.length === 0) {
      return;
    }
  
    const ticids = Array.from(
      new Set(
        ticketsList
          .map(t => String(t.TICID || t.ticid || '').trim())
          .filter(Boolean)
      )
    );
  
    if (ticids.length === 0) {
      return;
    }
  
    const payload = {
      type: "subscribe_many",
      ticids
    };
  
    socket.send(JSON.stringify(payload));
  
    addWebSocketLog('SUBSCRIBE_MANY', {
      ticidCount: ticids.length,
      ticids
    });
  };
  const connect = () => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      return;
    }

    if (isReconnectingRef.current) {
      
      return;
    }

    if (socketRef.current) {
      console.log(socketRef);
      try {
        socketRef.current.onclose = null;
        socketRef.current.onerror = null;
        socketRef.current.onopen = null;
        socketRef.current.onmessage = null;
        if (socketRef.current.readyState !== WebSocket.CLOSED) {
          socketRef.current.close();
        }
      } catch (e) {
      }
    }

    try {
      const ws = new WebSocket(wsUrl);
      socketRef.current = ws;
      ws.onopen = () => {
        
        setConnected(true);
        isReconnectingRef.current = false;
        reconnectAttemptsRef.current = 0;
        processedEventsRef.current.clear();
        
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        sendTicketSubscriptions(ws, tickets);
        addWebSocketLog('CONNECTION', {
          status: 'OPEN',
          url: wsUrl,
          timestamp: new Date().toISOString(),
          connectionId: Date.now(),
          readyState: ws.readyState
        });
      };

      ws.onmessage = (event) => {
        const parsed = safeParseJson(event.data, 200000);

        console.log(parsed);
        if (!parsed.ok || !parsed.value || typeof parsed.value !== 'object') {
          return;
        }
        const data = parsed.value;

        if (data && data.id) {
          ws.send(JSON.stringify({ type: "ack", id: data.id }));
        }

        if (data.type === "event_batch") {
          handleEventBatch(data);
        } else if (data.type === "event_update") {
          if (!data.data || typeof data.data !== 'object' || !data.data.ticid) {
            return;
          }

          const incomingEventId = data.data?.eveid;
          if (incomingEventId && processedEventsRef.current.has(String(incomingEventId))) {
            return;
          }

          const messageTicketId = String(data.data.ticid);
          const currentTicketId = selectedTicketIdRef.current ? String(selectedTicketIdRef.current) : null;
          
          const isCurrentTicket = currentTicketId && parseInt(currentTicketId) === parseInt(messageTicketId);

          // Ticket listesini güncelle (tüm ticket'lar için)
          setTickets(prevTickets => {
            return prevTickets.map(ticket => {
              if (String(ticket.TICID) === messageTicketId) {
                return {
                  ...ticket,
                  lastEventDate: data.data.timestamp || new Date().toISOString(),
                  LAST_EVENTDATE: data.data.timestamp || new Date().toISOString()
                };
              }
              return ticket;
            });
          });

          moveTicketToTop(messageTicketId);

          if (!isCurrentTicket) {
            setNotificationCounters(prev => {
              const newCounters = {
                ...prev,
                [messageTicketId]: (prev[messageTicketId] || 0) + 1
              };
              return newCounters;
            });
            // Event'i işaretle ama fetch etme
            processedEventsRef.current.add(String(incomingEventId));
            return; // Seçili ticket değilse, toolbar/body/sendbox güncellemesi yapma
          }

          // Seçili ticket ise, toolbar/body/sendbox güncellemesi yap
          
          setEveId(data.data.eveid);
          setTicId(data.data.ticid);
          setVisData(data.data.visible);
          setLoekz(data.data.loekz);

          markSendPerformanceStage(messageTicketId, 'wsMessage', {
            streamerId: data.data?.streamer_id || data.data?.STREAMER_ID || null,
            eventCount: 1,
          });

          processedEventsRef.current.add(String(incomingEventId));
          
          const wsLoekzValue = data.data.loekz;
          const wsVisibleValue = data.data.visible;

          const wsIsDeleted = wsLoekzValue === 1 || wsLoekzValue === "1" || wsLoekzValue === true ||
                              wsLoekzValue === "X" || wsLoekzValue === "x";
          const wsIsExplicitlyInvisible = wsVisibleValue !== undefined && wsVisibleValue !== null &&
                              (wsVisibleValue === 0 || wsVisibleValue === "0" || wsVisibleValue === false);

          if (wsIsDeleted || wsIsExplicitlyInvisible) {
            deleteEventFromCache(data.data.ticid, data.data.eveid);
            return;
          }

          isProcessingRef.current = true;

          fetchEventFromServer(
            data.data.ticid,
            data.data.eveid,
            data.data.streamer_id || data.data.STREAMER_ID || null
          ).finally(() => {
            isProcessingRef.current = false;
          });
        } else if (data.type === "event_delete") {
          if (!data.data?.ticid) {
            return;
          }

          const messageTicketId = String(data.data.ticid);
          const currentTicketId = selectedTicketIdRef.current ? String(selectedTicketIdRef.current) : null;

          const isCurrentTicket = currentTicketId && parseInt(currentTicketId) === parseInt(messageTicketId);

          if (isCurrentTicket) {
            deleteEventFromCache(data.data.ticid, data.data.eveid);
          }
        } else if (data.type === "ticket_update") {
          if (!data.data?.ticid) {
            return;
          }

          const updatedTicketId = String(data.data.ticid);
          const currentTicketId = selectedTicketIdRef.current ? String(selectedTicketIdRef.current) : null;
          
          if (data.data.LOEKZ === "X" || data.data.LOEKZ === "x" || data.data.TSTAT === "0" || data.data.TSTAT === 0) {
            setTickets(prevTickets => 
              prevTickets.filter(ticket => String(ticket.TICID) !== updatedTicketId)
            );
            
            if (currentTicketId && parseInt(currentTicketId) === parseInt(updatedTicketId)) {
              setEvents([]);
              setSelectedTicket(null);
              setSelectedTicketId(null);
              setRightPanelToolbarElements([]);
              setAllFields(null);
              setCurrentInputField(null);
            }
            
            return;
          }
          
          if (data.data.LAST_EVENTDATE) {
            moveTicketToTop(data.data.ticid);
          }

          setTickets((prev) => {
            const tid = String(data.data.ticid);
            const idx = prev.findIndex((t) => String(t.TICID) === tid);
            if (idx === -1) return prev;
            const updated = { ...prev[idx] };
            if (data.data.TSTAT !== undefined) updated.TSTAT = data.data.TSTAT;
            if (data.data.LAST_EVENTDATE) updated.LAST_EVENTDATE = data.data.LAST_EVENTDATE;
            if (data.data.TDESC !== undefined) updated.TDESC = data.data.TDESC;
            const next = [...prev];
            next[idx] = updated;
            return next;
          });
        } else if (data.type === "ticket_delete") {
          setTickets((prev) =>
            prev.filter((t) => String(t.TICID) !== String(data.data?.ticid)),
          );
        } else {
        }
      };

      ws.onclose = (event) => {
        setConnected(false);
        addWebSocketLog('CONNECTION', {
          status: 'CLOSED',
          code: event.code,
          reason: event.reason,
          timestamp: new Date().toISOString(),
          wasClean: event.wasClean,
          connectionId: Date.now()
        });

        // Eğer temiz bir kapanış değilse ve reconnect yapılması gerekiyorsa, yeniden bağlan
        // Code 1000 = Normal Closure (temiz kapanış), bu durumda reconnect yapma
        // Code 1006 = Abnormal Closure (anormal kapanış), reconnect yap
        if (event.code !== 1000 && shouldReconnectRef.current && !isReconnectingRef.current) {
          scheduleReconnect();
        }
      };

      ws.onerror = (error) => {
        setConnected(false);
        // onerror'da error.message genellikle undefined olur, bu yüzden sadece log'la
        // Console'a sürekli hata basmamak için sadece bir kez log'la
        if (!isReconnectingRef.current) {
          // Hata durumunda reconnect yap (eğer zaten reconnect yapılmıyorsa)
          if (shouldReconnectRef.current && !isReconnectingRef.current) {
            scheduleReconnect();
          }
        }
      };

    } catch (error) {
      setConnected(false);
      isReconnectingRef.current = false;
      
      // Bağlantı kurulurken hata olursa reconnect yap
      if (shouldReconnectRef.current && !isReconnectingRef.current) {
        scheduleReconnect();
      }
    }
  };

  // Reconnect mekanizması - Exponential backoff ile
  const scheduleReconnect = () => {
    // Eğer zaten reconnect yapılıyorsa veya reconnect durdurulmuşsa, yapma
    if (isReconnectingRef.current || !shouldReconnectRef.current) {
      return;
    }

    // Maksimum 10 deneme (yaklaşık 5 dakika sonra durur)
    if (reconnectAttemptsRef.current >= 10) {
      isReconnectingRef.current = false;
      return;
    }

    isReconnectingRef.current = true;
    reconnectAttemptsRef.current += 1;

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s, 64s, 128s, 256s, 512s
    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 30000); // Maksimum 30 saniye

    addWebSocketLog('RECONNECT', {
      attempt: reconnectAttemptsRef.current,
      delay: delay,
      timestamp: new Date().toISOString()
    });

    reconnectTimeoutRef.current = setTimeout(() => {
      if (shouldReconnectRef.current) {
        isReconnectingRef.current = false;
        connect();
      }
    }, delay);
  };
  useEffect(() => {

    if (!socketRef.current) {
      return;
    }
  
    if (socketRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
  
    if (!Array.isArray(tickets) || tickets.length === 0) {
      return;
    }
  
    sendTicketSubscriptions(socketRef.current, tickets);
  
  }, [tickets]);
  useEffect(() => {
    shouldReconnectRef.current = true;
    
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      return;
    }

    connect();

    return () => {
      // Component unmount olduğunda reconnect'i durdur
      shouldReconnectRef.current = false;
      
      // Reconnect timeout'unu temizle
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Micro-batch flush timer'ını temizle (bekleyen kuyruk düşürülür — UI yok)
      if (flushFetchTimerRef.current) {
        clearTimeout(flushFetchTimerRef.current);
        flushFetchTimerRef.current = null;
      }
      pendingFetchByTicketRef.current = {};

      // WebSocket bağlantısını kapat
      if (socketRef.current) {
        try {
          socketRef.current.onclose = null;
          socketRef.current.onerror = null;
          socketRef.current.onopen = null;
          socketRef.current.onmessage = null;
          if (socketRef.current.readyState !== WebSocket.CLOSED) {
            socketRef.current.close(1000, 'Component unmounting'); // Normal closure
          }
        } catch (e) {
          // Hata olursa önemli değil
        }
      }
    };
  }, []);

  const handleModalOk = () => {
    setShowModal(false);
    if (modalData && modalData.ticid) {
      selectTicket(modalData.ticid);
    }
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setModalData(null);
  };

  // Context value'yu useMemo ile sarmalayalım - infinite loop'u önlemek için
  const contextValue = useMemo(() => ({
    connected, 
    eveId, 
    ticId, 
    notificationCounters, 
    setNotificationCounters,
    visData,
    loekz,
    fetchEventFromServer,
    ticketsToMoveToTop,
    moveTicketToTop,
    pendingEvents,
    networkLogs,
    websocketLogs,
    showNetworkModal,
    setShowNetworkModal,
    logFilters,
    setLogFilters,
    addNetworkLog,
    addWebSocketLog
  }), [
    connected, 
    eveId, 
    ticId, 
    notificationCounters, 
    visData,
    loekz,
    fetchEventFromServer,
    ticketsToMoveToTop,
    moveTicketToTop,
    pendingEvents,
    networkLogs,
    websocketLogs,
    showNetworkModal,
    logFilters,
    addNetworkLog,
    addWebSocketLog
  ]);

  return (
    <TicketWebSocketContext.Provider value={contextValue}>
      {children}
    </TicketWebSocketContext.Provider>
  );
}; 